// Hand-rolled JSON tokenizer for CodeBlock's syntax coloring — no dependency
// (package.json has no prismjs/highlight.js/shiki, and none should be added
// for a JSON-only need). See dev-portal enterprise redesign spec §5.2.
//
// Classifies each token in an already `JSON.stringify(x, null, 2)`'d string
// so the caller can color object keys and number/boolean/null literals;
// strings and punctuation are left as plain text (same as the code block's
// base text color, so they need no wrapping span).
export type JsonTokenType = 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punct';

export interface JsonToken {
    text: string;
    type: JsonTokenType;
}

// Matches, in order of preference: a quoted string (with escape support),
// true/false, null, or a number (optional sign/decimal/exponent). Anything
// between matches (braces, brackets, colons, commas, whitespace) is plain
// punctuation and falls through untouched.
const JSON_TOKEN_RE = /"(?:\\.|[^"\\])*"|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

export function tokenizeJson(code: string): JsonToken[] {
    const tokens: JsonToken[] = [];
    let lastIndex = 0;
    // Array.from (not a direct for...of over the iterator) — this project's
    // tsconfig targets es5 without downlevelIteration, which only allows
    // for...of on real arrays, not arbitrary iterables like matchAll()'s result.
    for (const match of Array.from(code.matchAll(JSON_TOKEN_RE))) {
        const index = match.index ?? 0;
        const text = match[0];
        if (index > lastIndex) {
            tokens.push({ text: code.slice(lastIndex, index), type: 'punct' });
        }
        if (text.startsWith('"')) {
            // A string immediately followed by a colon (ignoring whitespace) is an
            // object key, not a value — re-inspect the source since matchAll gives
            // no lookahead capture.
            const isKey = /^\s*:/.test(code.slice(index + text.length));
            tokens.push({ text, type: isKey ? 'key' : 'string' });
        } else if (text === 'true' || text === 'false') {
            tokens.push({ text, type: 'boolean' });
        } else if (text === 'null') {
            tokens.push({ text, type: 'null' });
        } else {
            tokens.push({ text, type: 'number' });
        }
        lastIndex = index + text.length;
    }
    if (lastIndex < code.length) {
        tokens.push({ text: code.slice(lastIndex), type: 'punct' });
    }
    return tokens;
}
